'use client'

import {
  useAddWorkflowTagMutation,
  useGetWorkspaceTagsQuery,
  useRemoveWorkflowTagMutation,
} from '@/lib/features/workflow/workflowApi'
import { AnimatePresence, motion } from 'framer-motion'
import { forwardRef } from 'react'
import { toast } from 'react-toastify'
import { TagPicker } from './TagPicker'
import { TagPill } from './TagPill'

interface TagManagementDropdownProps {
  isOpen: boolean
  workflowId: string
  workspaceId: string
  currentTags: Array<{ id: string; name: string; workspaceId: string | null }>
}

export const TagManagementDropdown = forwardRef<HTMLDivElement, TagManagementDropdownProps>(
  ({ isOpen, workflowId, workspaceId, currentTags }, ref) => {
    const [addTag] = useAddWorkflowTagMutation()
    const [removeTag] = useRemoveWorkflowTagMutation()
    const { data: allTags = [] } = useGetWorkspaceTagsQuery(workspaceId)

    const handleAddTag = async (tagId: string) => {
      try {
        const tag = allTags.find((t) => t.id === tagId)
        if (!tag) {
          toast.error('Tag not found')
          return
        }
        await addTag({ workflowId, tagId, tag }).unwrap()
        toast.success('Tag added')
      } catch (error) {
        console.error('Failed to add tag:', error)
        toast.error('Failed to add tag')
      }
    }

    const handleRemoveTag = async (tagId: string) => {
      try {
        await removeTag({ workflowId, tagId }).unwrap()
        toast.success('Tag removed')
      } catch (error) {
        console.error('Failed to remove tag:', error)
        toast.error('Failed to remove tag')
      }
    }

    return (
      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={ref}
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="absolute left-0 top-0 z-30 w-80 max-w-[calc(100vw-32px)] rounded-2xl border border-gray-200/50 bg-white/95 p-4 shadow-xl backdrop-blur-[8px]"
            style={{
              maxHeight: 'calc(100vh - 32px)',
              overflowY: 'auto',
            }}
          >
            <div className="mb-3">
              <h3 className="mb-2 text-sm font-semibold text-gray-700">Current Tags</h3>
              {currentTags.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {currentTags.map((tag) => (
                    <TagPill
                      key={tag.id}
                      name={tag.name}
                      isGlobal={tag.workspaceId === null}
                      onRemove={() => handleRemoveTag(tag.id)}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-500">No tags yet</p>
              )}
            </div>

            <div className="border-t border-gray-200 pt-3">
              <h3 className="mb-2 text-sm font-semibold text-gray-700">Add Tags</h3>
              <TagPicker
                workspaceId={workspaceId}
                excludeTagIds={currentTags.map((tag) => tag.id)}
                onSelectTag={handleAddTag}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    )
  }
)

TagManagementDropdown.displayName = 'TagManagementDropdown'
